import { useState } from "react";
import { CalendarClock, Clock, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const CABECALHO = ["D", "S", "T", "Q", "Q", "S", "S"];
const HORAS = Array.from({ length: 13 }, (_, i) => String(9 + i).padStart(2, "0"));
const MINUTOS = ["00", "15", "30", "45"];

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const hojeIso = () => iso(new Date());

/**
 * Dois botoes compactos ("Hoje" e "10:00") que abrem, no clique, um CALENDARIO
 * de mes pra data e duas colunas de HORAS (9-21) e MINUTOS (0/15/30/45) pro
 * horario — formato pedido pelo dono depois de reprovar chips e selects.
 */
export default function AgendarPicker({ dataAg, setDataAg, horaAg, setHoraAg, minAg, setMinAg }) {
  const [picker, setPicker] = useState(null); // null | "data" | "hora"
  const [mes, setMes] = useState(() => { const d = new Date(); d.setDate(1); return d; });

  const hoje = hojeIso();
  const rotuloData = (() => {
    if (dataAg === hoje) return "Hoje";
    const amanha = new Date(); amanha.setDate(amanha.getDate() + 1);
    if (dataAg === iso(amanha)) return "Amanhã";
    return dataAg.split("-").reverse().slice(0, 2).join("/");
  })();

  const primeiroDia = new Date(mes.getFullYear(), mes.getMonth(), 1).getDay();
  const totalDias = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
  const mesAtualEhOCorrente =
    mes.getFullYear() === new Date().getFullYear() && mes.getMonth() === new Date().getMonth();

  const mudarMes = (delta) => setMes((m) => {
    const novo = new Date(m); novo.setMonth(m.getMonth() + delta); return novo;
  });

  const horaPassou = (h, m) => {
    if (dataAg !== hoje) return false;
    const agora = new Date();
    return parseInt(h, 10) < agora.getHours() ||
      (parseInt(h, 10) === agora.getHours() && parseInt(m, 10) <= agora.getMinutes());
  };

  const botao = "inline-flex items-center gap-1.5 rounded-lg border bg-background px-2.5 py-1.5 text-xs font-medium hover:border-primary transition-colors";

  return (
    <div className="flex items-center gap-1.5">
      {/* ------------------------------------------------------------ data */}
      <div className="relative">
        <button onClick={() => setPicker(picker === "data" ? null : "data")} className={botao}>
          <CalendarClock className="w-3 h-3 text-primary" /> {rotuloData}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
        {picker === "data" && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setPicker(null)} />
            <div className="absolute z-20 mt-1 w-60 rounded-xl border bg-background shadow-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => mudarMes(-1)} disabled={mesAtualEhOCorrente}
                  className="rounded-md p-1 hover:bg-muted disabled:opacity-25 disabled:cursor-not-allowed">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-semibold capitalize">
                  {MESES[mes.getMonth()]} {mes.getFullYear()}
                </span>
                <button onClick={() => mudarMes(1)} className="rounded-md p-1 hover:bg-muted">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-0.5 text-center">
                {CABECALHO.map((d, i) => (
                  <span key={i} className="text-[10px] font-semibold text-muted-foreground py-1">{d}</span>
                ))}
                {Array.from({ length: primeiroDia }, (_, i) => <span key={`v${i}`} />)}
                {Array.from({ length: totalDias }, (_, i) => {
                  const dia = i + 1;
                  const diaIso = iso(new Date(mes.getFullYear(), mes.getMonth(), dia));
                  const passado = diaIso < hoje;
                  const selecionado = diaIso === dataAg;
                  const ehHoje = diaIso === hoje;
                  return (
                    <button key={dia} disabled={passado}
                      onClick={() => { setDataAg(diaIso); setPicker(null); }}
                      className={`h-7 w-7 mx-auto rounded-md text-[11px] tabular-nums transition-colors
                        disabled:opacity-25 disabled:cursor-not-allowed ${
                        selecionado ? "bg-primary text-primary-foreground font-semibold"
                        : ehHoje ? "ring-1 ring-primary text-primary" : "hover:bg-muted"}`}>
                      {dia}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ------------------------------------------------------------ hora */}
      <div className="relative">
        <button onClick={() => setPicker(picker === "hora" ? null : "hora")}
          className={`${botao} tabular-nums`}>
          <Clock className="w-3 h-3 text-primary" /> {horaAg}:{minAg}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
        {picker === "hora" && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setPicker(null)} />
            <div className="absolute z-20 mt-1 rounded-xl border bg-background shadow-xl p-2 flex gap-2">
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground text-center mb-1">Hora</p>
                <div className="max-h-44 overflow-y-auto pr-0.5 space-y-0.5">
                  {HORAS.map((h) => (
                    <button key={h} disabled={horaPassou(h, "45")}
                      onClick={() => setHoraAg(h)}
                      className={`block w-12 rounded-md px-2 py-1 text-[11px] tabular-nums transition-colors
                        disabled:opacity-25 disabled:cursor-not-allowed ${
                        horaAg === h ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                      {h}h
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-l pl-2">
                <p className="text-[10px] font-semibold text-muted-foreground text-center mb-1">Min</p>
                <div className="space-y-0.5">
                  {MINUTOS.map((m) => (
                    <button key={m} disabled={horaPassou(horaAg, m)}
                      onClick={() => { setMinAg(m); setPicker(null); }}
                      className={`block w-12 rounded-md px-2 py-1 text-[11px] tabular-nums transition-colors
                        disabled:opacity-25 disabled:cursor-not-allowed ${
                        minAg === m ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                      :{m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
